// The "why are you taking this listing down?" survey. Best-practice shape:
// one single-select question with six options asked at the moment of leaving,
// one optional detail, and a single targeted alternative chosen from the
// answer, the flow is data (recommendedAlternative), the dialog just renders.

export type DelistReason =
  | "found_here"
  | "found_elsewhere"
  | "not_renting_now"
  | "few_requests"
  | "duplicate_or_mistake"
  | "other";

/** How the owner got into the flow: which options make sense first. */
export type DelistIntent = "delete" | "pause" | "found_tenant";

/** Stored outcome (listing_delist_events.action). */
export type DelistAction = "undecided" | "kept_published" | "paused" | "rented" | "deleted";

/** The one alternative we put in front of the owner for a given reason. */
export type DelistAlternative = "keep_published" | "pause" | "review" | "delete";

export interface DelistReasonOption {
  code: DelistReason;
  label: (siteName: string) => string;
  hint?: string;
}

export const DELIST_REASONS: DelistReasonOption[] = [
  { code: "found_here", label: (site) => `I found a tenant through ${site}` },
  { code: "found_elsewhere", label: () => "I found a tenant somewhere else" },
  { code: "not_renting_now", label: () => "I'm not renting it out for now", hint: "Own use, renovation, selling…" },
  { code: "few_requests", label: () => "Not enough (or not the right) requests" },
  { code: "duplicate_or_mistake", label: () => "This listing is a duplicate or a mistake" },
  { code: "other", label: () => "Something else" },
];

export const reasonsForIntent = (intent: DelistIntent): DelistReasonOption[] =>
  intent === "found_tenant"
    ? DELIST_REASONS.filter((r) => r.code === "found_here" || r.code === "found_elsewhere")
    : DELIST_REASONS;

export type FoundVia = "airbnb_booking" | "facebook" | "other_site" | "agency" | "word_of_mouth" | "other";

export const FOUND_VIA_OPTIONS: { code: FoundVia; label: string }[] = [
  { code: "airbnb_booking", label: "Airbnb or Booking.com" },
  { code: "facebook", label: "Facebook groups" },
  { code: "other_site", label: "Another rentals website" },
  { code: "agency", label: "An agency or property manager" },
  { code: "word_of_mouth", label: "Word of mouth" },
  { code: "other", label: "Somewhere else" },
];

const ALTERNATIVE: Record<DelistReason, DelistAlternative> = {
  found_here: "keep_published",
  found_elsewhere: "keep_published",
  not_renting_now: "pause",
  few_requests: "review",
  duplicate_or_mistake: "delete",
  other: "pause",
};

export const recommendedAlternative = (reason: DelistReason): DelistAlternative => ALTERNATIVE[reason];

/** Short labels for admin charts (metrics reason mix). */
export const DELIST_REASON_SHORT: Record<DelistReason, string> = {
  found_here: "Found a tenant here",
  found_elsewhere: "Found a tenant elsewhere",
  not_renting_now: "Not renting for now",
  few_requests: "Too few requests",
  duplicate_or_mistake: "Duplicate or mistake",
  other: "Other",
};

export const DELIST_ACTION_LABELS: Record<DelistAction, string> = {
  undecided: "Undecided",
  kept_published: "Kept published",
  paused: "Paused",
  rented: "Marked rented",
  deleted: "Deleted",
};

export const isDelistReason = (s: string): s is DelistReason => s in ALTERNATIVE;
